import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary, ReloadErrorFallback } from '@zeal/web-ui';

import UnAuthOwnApp from './components/UnAuthOwnApp';
import UnAuthorized from './components/UnAuthorized';
import { useOwnAppAccess, useOwnAppProvider } from './useOwnApp';
import { Content, MainGrid } from './components/StyledComponents';
import { AppContainerLoading } from './components/AppContainerLoading';

import AppMenu from '../AppMenu';
import { useLoggedIn, useLoginData } from '../ACL/useLogin';
import { useAccountInfo } from '../BusinessAccount/useBusinessInfo';
import useGrants from './../ACL/useGrants';
import NavBar from './NavBar';
import { useAppState } from '../store/appStore';
import { usePageStore } from '@app/store/pageStore';
import { getAppRoutes } from '@app/utils/appRoutes';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuthStore, useLoginVault } from '@app/store/authStore';
import { ROUTES } from '@app/AppRoutes/routes';
import { usePusherSubscription } from '../Pusher/usePusherSubscription';
import { BannerManager, useBannerState } from './components/Banners';
import { UpgradeSuccessModal } from '../../UpgradePlan/components/UpgradeSuccess/UpgradeSuccessModal';
import { usePaymentStore } from '@modules/UpgradePlan/components/paymentStore';
import { InternalModuleOptions } from '@app/types/types';

const mainGridVariants: { [key: string]: any } = {
	open: { '--main-navbar-width': '18.5rem' },
	minimized: { '--main-navbar-width': '5.5rem' },
};

const { ownAppMenus, appMainMenus } = getAppRoutes();

const appRoutes = [...ownAppMenus, ...appMainMenus];

function AppContainer() {
	const { i18n } = useTranslation();

	const authenticate = useAuthStore((state) => state.authenticate);
	const loggedInData = useLoginVault();
	const { data } = useLoginData();
	const navigate = useNavigate();

	const { bannerState, setIsVisible: setIsBannerVisible } = useBannerState();

	useEffect(() => {
		if (data) {
			// if (data?.type != loggedInData?.merchantType) {
			// 	// TODO: Update this to avoid NotFound page flickering before redirection
			// 	navigate(ROUTES.ROOT.path);
			// }

			const permissions = {
				permissions: data?.business_admin?.roles?.[0]?.permissions,
				cuuid: data.business_admin?.uuid,
			};
			authenticate(
				{
					...loggedInData,
					isSuperAdmin: data?.business_admin?.is_super_admin,
					uuid: data?.business_admin?.uuid,
					businessName: data?.business_admin?.business?.name,
					email: data?.business_admin?.email,
					type: data?.business_admin?.business?.type,
					isNIMerchant: data?.business_admin?.business?.type === 'ni',
					name: data?.business_admin?.name,
					isUkMerchant: data?.is_uk,
					merchantType: data?.type,
				},
				permissions
			);
		}
	}, [data]);

	const { pathname } = useLocation();
	const { appMenu: appMenuState } = usePageStore();

	// casted to `InternalModuleOptions[]` as `<AppContainer />` will be only be wrapped around Internal Modules (external:false)
	const pageParams = useMemo<InternalModuleOptions>(() => {
		return ((appRoutes || []) as InternalModuleOptions[]).find(
			(route) =>
				pathname === route.path ||
				!!route?.subMenu?.find((sub) => sub.path === pathname)
		)!;
	}, [pathname]);

	const isOwnApp = pageParams?.ownApp;
	const routeLabel = useMemo(() => {
		return (
			pageParams?.subMenu?.find((sub) => sub.path === pathname)?.label ||
			pageParams?.label
		);
	}, [pageParams, pathname]);

	useGrants();
	useAccountInfo();
	useOwnAppProvider(isOwnApp ?? false);
	const showUpgradePlanModal = usePaymentStore(
		(store) => store.showUpgradePlanModal
	);

	const { isLoggedIn } = useLoggedIn();
	const { isRouteAllowed } = useOwnAppAccess();
	const { isSwitchedOnLoad } = useAppState();
	const isMenuMini = useMemo(
		() => appMenuState === 'minimized',
		[appMenuState]
	);

	useEffect(() => {
		document.dir = i18n.dir();

		document.documentElement.setAttribute(
			'lang',
			i18n.language || i18n.resolvedLanguage || 'en-GB'
		);
	}, [i18n, i18n.language]);

	const content = isRouteAllowed(isOwnApp) ? <Outlet /> : <UnAuthOwnApp />;

	usePusherSubscription();

	if (!isLoggedIn) {
		return <UnAuthorized />;
	}

	if (!isSwitchedOnLoad) {
		return <AppContainerLoading />;
	}

	return (
		<MainGrid
			animate={appMenuState}
			variants={mainGridVariants}
			initial={appMenuState}
			transition={{
				type: 'easeIn',
				duration: 0.2,
			}}
		>
			<NavBar
				routeLabel={routeLabel}
				bannerState={bannerState}
				setIsBannerVisible={setIsBannerVisible}
			/>
			<BannerManager
				bannerState={bannerState}
				setIsVisible={setIsBannerVisible}
			/>
			<UpgradeSuccessModal isOpen={showUpgradePlanModal} />
			<AppMenu gridArea="menu" isCollapsed={isMenuMini} />

			<React.Suspense fallback={<AppContainerLoading />}>
				<Content id="app-container-content">
					<ErrorBoundary fallback={<ReloadErrorFallback />}>
						{content}
					</ErrorBoundary>
				</Content>
			</React.Suspense>
		</MainGrid>
	);
}

export default memo(AppContainer);
